"use client";

// src/input.tsx
import * as React4 from "react";
import {
  systemForwardRef as systemForwardRef4,
  useStyleConfig as useStyleConfig2
} from "@stepstone-genesis/system";
import { mergeRefs } from "@stepstone-genesis/hooks-use-merged-refs";
import { Text } from "@stepstone-genesis/components-text";
import {
  useFormStateHandlerContext,
  useFormStateHandler
} from "@stepstone-genesis/components-form-state-handler";

// src/container.tsx
import * as React3 from "react";
import {
  systemForwardRef as systemForwardRef3,
  useStyleConfig,
  mergeStyles,
  useTheme
} from "@stepstone-genesis/system";
import { Flex as Flex3 } from "@stepstone-genesis/components-flex";

// src/suffix.tsx
import * as React from "react";
import { systemForwardRef } from "@stepstone-genesis/system";
import { Flex } from "@stepstone-genesis/components-flex";
var FormInputSuffix = systemForwardRef(
  function FormInputSuffix2({ children, ...props }, ref) {
    if (children == null)
      return null;
    return /* @__PURE__ */ React.createElement(
      Flex,
      {
        flxItems: "center",
        ref,
        __root: {
          minWidth: "auto",
          color: "colorGreyDark",
          borderBottomRightRadius: "radiusM",
          borderRightColor: "colorBorderDefault",
          borderRightStyle: "none",
          borderRightWidth: "1px",
          borderTopRightRadius: "radiusM",
          lineHeight: "inherit"
        },
        ...props
      },
      children
    );
  }
);

// src/prefix.tsx
import * as React2 from "react";
import { systemForwardRef as systemForwardRef2 } from "@stepstone-genesis/system";
import { Flex as Flex2 } from "@stepstone-genesis/components-flex";
var FormInputPrefix = systemForwardRef2(
  function FormInputPrefix2({ children, ...props }, ref) {
    if (children == null)
      return null;
    return /* @__PURE__ */ React2.createElement(
      Flex2,
      {
        flxItems: "center",
        flxJustify: "center",
        ref,
        __root: {
          minWidth: "auto",
          color: "colorGreyDark",
          borderBottomLeftRadius: "radiusM",
          borderRightColor: "colorBorderDefault",
          borderRightStyle: "none",
          borderRightWidth: "1px",
          borderTopLeftRadius: "radiusM",
          lineHeight: "inherit",
          paddingLeft: "spaceM",
          paddingRight: "spaceNone"
        },
        ...props
      },
      children
    );
  }
);

// src/styles.ts
var root = {
  appearance: "none",
  background: "transparent",
  borderRadius: "radiusM",
  boxShadow: "boxShadowNone",
  color: "colorTextPrimaryDefault",
  display: "block",
  margin: "spaceNone",
  outline: "none",
  paddingX: "spaceM",
  resize: "none",
  width: "100%",
  cursor: "auto",
  border: "none",
  _hidden: {
    display: "none"
  },
  _disabled: {
    cursor: "not-allowed",
    color: "colorTextDisabledDefault",
    _placeholder: {
      color: "colorTextDisabledDefault"
    }
  },
  _placeholder: {
    color: "colorTextTertiaryDefault"
  },
  _readOnly: {
    color: "colorBlack"
  },
  // DO-1256 override the browser autofill styles
  // prevent the browser from changing the input's colors
  // by delaying the change "forever"
  ":-webkit-autofill, :autofill": {
    transition: "color 0s step-end 5000000s, background-color 0s step-end 5000000s"
  }
};
var container = (theme) => {
  var _a, _b;
  return {
    position: "relative",
    backgroundColor: "colorWhite",
    cursor: "text",
    borderRadius: "radiusM",
    color: "colorGreyLighter",
    borderStyle: "solid",
    borderWidth: "1px",
    _hidden: {
      display: "none"
    },
    _invalid: {
      color: "colorStatusNegativeDefault"
    },
    _hover: {
      borderColor: "colorGreyLight",
      _invalid: {
        borderColor: "colorStatusNegativeDefault"
      }
    },
    _focus: {
      outline: "outlineDefault",
      outlineOffset: `${(_a = theme.space) == null ? void 0 : _a.componentInputFocusOutlineOffset}`,
      boxShadow: "boxShadowFocusWhite"
    },
    _focusWithIn: {
      outline: "outlineDefault",
      outlineOffset: `${(_b = theme.space) == null ? void 0 : _b.componentInputFocusOutlineOffset}`,
      boxShadow: "boxShadowFocusWhite",
      borderColor: "componentInputFocusBorderColor"
    },
    _active: {
      borderColor: "componentInputActiveBorderColor"
    },
    _disabled: {
      cursor: "not-allowed",
      bg: "colorBackgroundDisabledDefault",
      borderColor: "colorGreyLight",
      _hover: {
        borderColor: "colorGreyLight"
      }
    },
    _readOnly: {
      bg: "colorOffWhite",
      _hover: {
        color: "colorGreyLighter"
      }
    },
    // DO-1256 override the browser autofill styles
    // if there's an autofilled input within,
    // change bg to out custom color
    ":has(> :-webkit-autofill, > :autofill)": {
      backgroundColor: "colorHighlightLight"
    }
  };
};
var sizes = ({ theme }) => {
  var _a, _b, _c;
  return {
    small: {
      paddingY: "spaceXs",
      fontSize: "fontSizeM",
      lineHeight: "lineHeightS",
      minHeight: `calc(${(_a = theme.sizes) == null ? void 0 : _a.size40} - 2px)`
    },
    medium: {
      paddingY: "spaceS",
      fontSize: "fontSizeM",
      lineHeight: "lineHeightM",
      minHeight: "size50"
    },
    large: {
      paddingY: `calc(${(_b = theme.sizes) == null ? void 0 : _b.size20} - 2px)`,
      fontSize: "fontSizeL",
      lineHeight: "lineHeightM",
      minHeight: `calc(${(_c = theme.sizes) == null ? void 0 : _c.size70} - 2px)`
    }
  };
};
var inputButton = {
  minWidth: "auto",
  paddingLeft: 0,
  paddingRight: 0,
  marginRight: "spaceM",
  _focus: {
    boxShadow: "boxShadowNone"
  }
};
var inputButtonSize = {
  medium: {
    width: "size30",
    height: "size30"
  },
  large: {
    width: "size50",
    height: "size50"
  }
};
var styles = {
  root,
  sizes,
  container,
  inputButton,
  inputButtonSize
};

// src/container.tsx
var FormInputContainer = systemForwardRef3(function FormInputContainer2({
  children,
  prefix,
  suffix,
  inputContainerGroupStyles,
  unstable_container_class_name,
  sx,
  isInverted = false,
  ...props
}, ref) {
  const displayBorder = {
    borderColor: isInverted ? "transparent" : "colorBorderDefault"
  };
  const { theme } = useTheme();
  const { FORM_INPUT_CONTAINER } = useStyleConfig("FORM_INPUT_CONTAINER", {
    styles: {
      base: mergeStyles([styles.container(theme), displayBorder])
    }
  });
  return /* @__PURE__ */ React3.createElement(
    Flex3,
    {
      ref,
      className: unstable_container_class_name,
      __root: FORM_INPUT_CONTAINER.styles,
      sx: mergeStyles([inputContainerGroupStyles, sx]),
      element: "FORM_INPUT_CONTAINER",
      ...props
    },
    prefix && /* @__PURE__ */ React3.createElement(FormInputPrefix, null, prefix),
    children,
    suffix && /* @__PURE__ */ React3.createElement(FormInputSuffix, null, suffix)
  );
});

// src/input.tsx
var FormTextInput = systemForwardRef4(function FormTextInput2({
  type = "text",
  context,
  suffix,
  size = "medium",
  isWrappedInContainer = true,
  prefix,
  inputContainerGroupStyles,
  unstable_focus = true,
  unstable_container_class_name,
  isInverted,
  ...props
}, ref) {
  const { FORM_INPUT } = useStyleConfig2("FORM_INPUT", {
    styles: {
      base: styles.root,
      responsiveVariants: [
        {
          id: "form-input-size",
          prop: size,
          variants: styles.sizes
        }
      ]
    }
  });
  const inputRef = React4.useRef(null);
  const mergedInputRefs = mergeRefs(ref, inputRef);
  const ctx = context == null ? void 0 : context.formElementProps(
    props,
    mergedInputRefs
  );
  const { onClick } = context == null ? void 0 : context.formElementFocus(props, mergedInputRefs);
  const wordCountHandler = (context == null ? void 0 : context.isCharacterCountVisible) ? context == null ? void 0 : context.formCharacterCount(ctx).inputChangeHandler : void 0;
  const input = React4.useMemo(() => {
    return /* @__PURE__ */ React4.createElement(
      Text,
      {
        as: "input",
        element: "FORM_INPUT",
        ref: mergedInputRefs,
        type,
        __root: {
          ...FORM_INPUT.styles,
          ...prefix && { paddingLeft: "spaceS" },
          ...suffix && { paddingRight: "spaceNone" }
        },
        ...props,
        ...ctx,
        ...wordCountHandler,
        size: "medium"
      }
    );
  }, [
    ctx,
    FORM_INPUT.styles,
    prefix,
    props,
    suffix,
    type,
    wordCountHandler,
    mergedInputRefs
  ]);
  return isWrappedInContainer ? /* @__PURE__ */ React4.createElement(
    FormInputContainer,
    {
      suffix,
      prefix,
      onClick: unstable_focus ? onClick : void 0,
      inputContainerGroupStyles,
      isInverted,
      unstable_container_class_name,
      ...context == null ? void 0 : context.dataAttributes(ctx)
    },
    input
  ) : input;
});
var FormTextInputNonContext = systemForwardRef4(
  function FormTextInputNonContext2({ size, ...props }, ref) {
    const nonContextFormStateHandler = useFormStateHandler(props);
    return /* @__PURE__ */ React4.createElement(
      FormTextInput,
      {
        context: nonContextFormStateHandler,
        size,
        ref,
        ...props
      }
    );
  }
);
var FormInput = systemForwardRef4(
  function FormInput2(props, ref) {
    const withinContextFormStateHandler = useFormStateHandlerContext();
    return withinContextFormStateHandler ? /* @__PURE__ */ React4.createElement(
      FormTextInput,
      {
        context: withinContextFormStateHandler,
        size: withinContextFormStateHandler == null ? void 0 : withinContextFormStateHandler.size,
        ref,
        ...props
      }
    ) : /* @__PURE__ */ React4.createElement(FormTextInputNonContext, { ref, ...props });
  }
);
FormInput.id = "input";

// src/button.tsx
import * as React5 from "react";
import {
  mergeStyles as mergeStyles2,
  systemForwardRef as systemForwardRef5,
  useStyleConfig as useStyleConfig3
} from "@stepstone-genesis/system";
import { Button } from "@stepstone-genesis/components-button";
import {
  useFormStateHandlerContext as useFormStateHandlerContext2,
  useFormStateHandler as useFormStateHandler2
} from "@stepstone-genesis/components-form-state-handler";
var FormInputButtonElement = systemForwardRef5(function FormInputButtonElement2({ children, context, size, ...props }, ref) {
  const ctxFocus = context == null ? void 0 : context.formElementFocus(props, ref);
  const ctxDataTags = context == null ? void 0 : context.dataAttributes(props);
  const ctxSize = (context == null ? void 0 : context.size) || "medium";
  const { FORM_INPUT_BUTTON } = useStyleConfig3("FORM_INPUT_BUTTON", {
    styles: {
      base: styles.inputButton,
      responsiveVariants: [
        {
          id: "form-input-button-size",
          prop: size || ctxSize,
          variants: styles.inputButtonSize
        }
      ]
    }
  });
  return /* @__PURE__ */ React5.createElement(
    Button,
    {
      ref,
      element: "FORM_INPUT_BUTTON",
      variant: "linkSecondary",
      paddingX: "spaceM",
      ...props,
      sx: mergeStyles2([FORM_INPUT_BUTTON.styles, props.sx]),
      ...ctxFocus,
      ...ctxDataTags
    },
    children
  );
});
var FormInputButtonNonContext = systemForwardRef5(function FormInputButtonNonContext2(props, ref) {
  const nonContextFormStateHandler = useFormStateHandler2(props);
  return /* @__PURE__ */ React5.createElement(
    FormInputButtonElement,
    {
      context: nonContextFormStateHandler,
      ref,
      ...props
    }
  );
});
var FormInputButton = systemForwardRef5(
  function FormInputButton2(props, ref) {
    const withinContextFormStateHandler = useFormStateHandlerContext2();
    return withinContextFormStateHandler ? /* @__PURE__ */ React5.createElement(
      FormInputButtonElement,
      {
        context: withinContextFormStateHandler,
        ref,
        size: props.size,
        ...props
      }
    ) : /* @__PURE__ */ React5.createElement(FormInputButtonNonContext, { ref, size: props.size, ...props });
  }
);
export {
  FormInput,
  FormInputButton,
  FormInputContainer,
  FormInputPrefix,
  FormInputSuffix,
  FormTextInput,
  styles as inputStyles
};
